import { assign, createMachine, MachineConfig } from 'xstate';
import creditCardStates, {
  CreditCardEvent,
  CreditCardContext,
} from '../submitCase/creditCard';
import selectPaymentTypeStates, {
  PaymentType,
  SelectPaymentTypeContext,
  SelectPaymentTypeEvent,
} from '../submitCase/selectPaymentType';
import * as actions from '../submitCase/actions';
import * as guards from '../submitCase/guards';
import { getFunctions } from 'firebase/functions';
import * as cloudFunctions from '../../api/cloudFunctions';

export type EditPaymentContext = CreditCardContext & SelectPaymentTypeContext;

export type EditPaymentEvent = CreditCardEvent | SelectPaymentTypeEvent;

async function editPayment(context: EditPaymentContext) {
  const invoicingEnabled = context.paymentType === PaymentType.INVOICE;

  const fns = getFunctions(context.firebaseApp);
  const result = await cloudFunctions.updateCase(fns, {
    case: {
      ...context.case!,
      invoiceAccount: undefined, // need to cancel this out here since added on backend
      billToParentAccount:
        invoicingEnabled &&
        context.selectedInvoiceAccountUid !== context.userId,
    },
    user: {
      id: context.userId,
      name: context.case!.user.displayName,
      email: context.case!.user.email,
    },
    invoiceAccount: invoicingEnabled
      ? context.selectedInvoiceAccountUid
      : undefined,
    paymentMethodId: invoicingEnabled
      ? undefined
      : context.selectedPaymentMethod,
    setupIntentId: invoicingEnabled ? undefined : context.setupIntent?.id,
  });

  return {
    case: result.case,
    invoice: result.invoice,
  };
}

const config: MachineConfig<EditPaymentContext, any, EditPaymentEvent> = {
  id: 'editPayment',
  initial: 'selectPaymentType',
  context: {
    firebaseApp: null as any,
    stripePublishableKey: '',
    userId: '',
    email: '',
    name: '',
    selectedInvoiceAccountUid: '',
    paymentType: PaymentType.CREDIT_CARD,
    invoices: [],
    hasLoadedInvoices: false,
    hasPastDueInvoice: false,
    invoicingDisabled: false,
  },
  states: {
    selectPaymentType: {
      ...(selectPaymentTypeStates as any),
      onDone: 'creditCard',
      on: {
        NEXT: 'creditCard',
      },
    },
    creditCard: {
      ...(creditCardStates as any),
      onDone: 'submit',
      on: {
        BACK: 'selectPaymentType',
      },
    },
    error: {
      on: {
        RETRY: 'selectPaymentType',
      },
    },
    submit: {
      invoke: {
        src: editPayment,
        onDone: {
          target: 'submitted',
          actions: assign({
            case: (context, event) => event.data.case,
          }),
        },
        onError: {
          target: 'error',
        },
      },
    },
    submitted: {
      type: 'final',
    },
  },
};

export const editPaymentMachine = createMachine<
  EditPaymentContext,
  EditPaymentEvent
>(config, {
  actions: actions as any,
  guards: {
    ...(guards as any),
    every: (ctx, event, { cond }) => {
      if (cond.type !== 'every') {
        throw new Error('Error: "every" condition expected type to be "every"');
      }
      return cond.guards.every((guardKey) => guards[guardKey](ctx, event));
    },
  },
});
